/**
 * useUserAudit — TanStack `useInfiniteQuery` over
 * `GET /organizations/:slug/users/:userId/audit?cursor&limit`.
 *
 * Status surface (closed):
 *   - 'loading'   — first request in flight or upstream not ready.
 *   - 'ready'     — at least one page resolved; `events` flattened.
 *   - 'forbidden' — 403.
 *   - 'notFound'  — 404 (anti-enum).
 *   - 'error'     — anything else.
 *
 * `fetchNext()` requests the next page using the most recent
 * `next_cursor`. `hasNext` mirrors the last page's `next_cursor !== null`.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { AuditEventRow } from './types.js';

const FORBIDDEN = Symbol('user-audit:forbidden');
const NOT_FOUND = Symbol('user-audit:notFound');

const PAGE_SIZE = 50;

export type UseUserAuditResult =
  | {
      readonly status: 'loading';
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      readonly events: readonly AuditEventRow[];
      readonly hasNext: boolean;
      readonly fetchNext: () => void;
      readonly isFetchingNext: boolean;
      readonly refetch: () => void;
    }
  | { readonly status: 'forbidden'; readonly refetch: () => void }
  | { readonly status: 'notFound'; readonly refetch: () => void }
  | { readonly status: 'error'; readonly refetch: () => void };

interface AuditDto {
  readonly id: string;
  readonly organization_id?: string;
  readonly actor_user_id: string;
  /**
   * Optional on the wire because the BE only started enriching this
   * after the chore/manual-qa-fixes work — a defensive `??` keeps the
   * FE robust if it ever sees a payload that predates the enrichment.
   */
  readonly actor_email?: string | null;
  readonly target_user_id: string;
  readonly event_type: string;
  readonly payload: unknown;
  readonly created_at: number;
}

interface AuditPage {
  readonly events: readonly AuditDto[];
  readonly next_cursor: string | null;
}

type PageResult = typeof FORBIDDEN | typeof NOT_FOUND | AuditPage;

function projectEvent(dto: AuditDto): AuditEventRow {
  return {
    id: dto.id,
    eventType: dto.event_type,
    actorUserId: dto.actor_user_id,
    actorEmail: dto.actor_email ?? null,
    targetUserId: dto.target_user_id,
    createdAt: dto.created_at,
    payload: dto.payload,
  };
}

export function buildUserAuditQueryKey(
  orgSlug: string | null,
  userId: string | undefined,
): readonly unknown[] {
  return ['identity', 'users', orgSlug, 'audit', userId];
}

export function useUserAudit(
  userId: string | undefined,
): UseUserAuditResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const enabled =
    orgSlug !== null && typeof userId === 'string' && userId.length > 0;

  const query = useInfiniteQuery<
    PageResult,
    Error,
    { pages: PageResult[]; pageParams: unknown[] },
    readonly unknown[],
    string | undefined
  >({
    queryKey: buildUserAuditQueryKey(orgSlug, userId),
    enabled,
    retry: false,
    initialPageParam: undefined,
    getNextPageParam: (last) => {
      if (last === FORBIDDEN || last === NOT_FOUND) return undefined;
      return last.next_cursor === null ? undefined : last.next_cursor;
    },
    queryFn: async ({ pageParam }) => {
      const query: Record<string, string | number> = { limit: PAGE_SIZE };
      if (typeof pageParam === 'string' && pageParam.length > 0) {
        query.cursor = pageParam;
      }
      const result = await apiClient.GET(
        '/organizations/{slug}/users/{userId}/audit',
        {
          params: {
            path: { slug: orgSlug as string, userId: userId as string },
            query: query as never,
          },
        },
      );
      if (result.response.status === 403) return FORBIDDEN;
      if (result.response.status === 404) return NOT_FOUND;
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /audit failed with status ${result.response.status}`,
        );
      }
      return result.data as AuditPage;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };
  const fetchNext = (): void => {
    void query.fetchNextPage();
  };

  if (!enabled || query.isPending) return { status: 'loading', refetch };
  if (query.isError) return { status: 'error', refetch };

  const pages = query.data?.pages ?? [];
  const first = pages[0];
  if (first === FORBIDDEN) return { status: 'forbidden', refetch };
  if (first === NOT_FOUND) return { status: 'notFound', refetch };

  const events: AuditEventRow[] = [];
  for (const page of pages) {
    if (page === FORBIDDEN || page === NOT_FOUND) continue;
    for (const e of page.events) events.push(projectEvent(e));
  }

  return {
    status: 'ready',
    events,
    hasNext: query.hasNextPage,
    fetchNext,
    isFetchingNext: query.isFetchingNextPage,
    refetch,
  };
}
